import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Slider, Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PublishIcon from '@material-ui/icons/Publish';
import SaveIcon from '@material-ui/icons/Save';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  changeMerchandisingRuleStatus,
  clearPreview,
  clearRuleEditorFeedback,
  deleteMerchandisingRule,
  fetchMerchandisingRule,
  fetchMerchandisingRules,
  fetchMerchandisingIndexState,
  runMerchandisingPreview,
  saveMerchandisingRule,
  searchMerchandisingValues,
  selectActiveRulePage,
} from '../state/merchandisingSlice';
import { resolveProducts, searchProducts } from '../../../slices/ProductSelectorSlice';
import { descriptorForCapabilities, preferredOperator, descriptorForSavedCondition, canonicalizeSavedConditionFields, filterIndexFields, humanField } from '../model/conditionEditor';
import ConditionBuilder from './ConditionBuilder';
import ConditionFieldPicker from './ConditionFieldPicker';
import {
  appendRankingCondition,
  defaultConditionValue,
  emptyRankingWorkbench,
  rankingExpressionValid,
  rankingSearchTermsValid,
  rankingRuleToWorkbench,
  rankingWorkbenchRule,
} from '../model/rankingWorkbench';
import {
  formatLocalDateTime, InlineNotice, PageHeader, PriorityLabel, ProductImage, ScheduleCard,
  SearchInput, SelectionCheckbox, StateBoundary, StatusChip, WorkbenchPagination,
} from './MerchandisingWorkbenchUi';
import RuleTransferControls from './RuleTransferControls';
import IndexStatusBar from './IndexStatusBar';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from './UnsavedChangesGuard';
import { CapabilityNotice } from './MerchandisingStatus';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useMerchandisingWorkbenchCopy } from './useMerchandisingWorkbenchCopy';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import SearchPhraseEditor from './SearchPhraseEditor';
import {
  rankingFactorMarks, rankingFactorToPosition, rankingPositionToFactor,
} from '../model/rankingFactorScale';
import {
  ruleUsesAdvancedConditions,
} from '../model/ruleCapabilities';
import { runWithConcurrency } from '../model/rulePortability';

export { descriptorForCapabilities, preferredOperator, descriptorForSavedCondition, canonicalizeSavedConditionFields, groupConditionOperatorOptions, conditionValueForOperatorChange, fallbackConditionValueLabel, filterIndexFields } from '../model/conditionEditor';
export { ConditionRow, ConditionValue, OperatorPicker } from './MerchandisingConditionControls';

const validSchedule = (from, to) => {
  if (from && Number.isNaN(Date.parse(from))) return false;
  if (to && Number.isNaN(Date.parse(to))) return false;
  return !(from && to) || Date.parse(to) > Date.parse(from);
};
const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
  });
};

const flattenConditions = (group) => (group?.items || []).flatMap((item) => (
  Array.isArray(item.items) ? flattenConditions(item) : [item]
));

const conditionLabelKey = (condition) => `${condition?.field || ''}:${(
  Array.isArray(condition?.value) ? condition.value : [condition?.value]
).filter((value) => value !== '' && value != null).map(String).join(',')}`;

const conditionValueLabel = (condition, labels = {}) => {
  const values = (Array.isArray(condition?.value) ? condition.value : [condition?.value])
    .filter((value) => value !== '' && value != null);
  if (!values.length) return '';
  const rendered = values.map((value) => labels[String(value)]).filter(Boolean);
  if (!rendered.length) return '';
  return rendered.length > 2 ? `${rendered.slice(0, 2).join(', ')} +${rendered.length - 2}` : rendered.join(', ');
};

export const rankingRuleTitle = (rule, schema = {}, labelsByCondition = {}, fallback = '') => {
  const customName = String(rule?.name || '').trim();
  if (customName && customName.toLowerCase() !== 'product ranking rule') return customName;
  const conditions = flattenConditions(rule?.conditions);
  const first = conditions[0];
  if (!first) return fallback || String(rule?.kind || '');
  const field = humanField(first.field, descriptorForSavedCondition(first, schema));
  const value = conditionValueLabel(
    first,
    labelsByCondition[first.__id]
      || labelsByCondition[conditionLabelKey(first)]
      || labelsByCondition[first.field]
      || {},
  );
  const suffix = conditions.length > 1 ? ` +${conditions.length - 1}` : '';
  return `${field}${value ? ` · ${value}` : ''}${suffix}`;
};

export const resolveRankingTitleLabels = async (dispatch, conditions, schema) => {
  const fields = [...new Set(conditions.map((condition) => condition.field))];
  const fieldResults = await runWithConcurrency(fields, async (field) => {
    const representative = conditions.find((condition) => condition.field === field);
    const descriptor = descriptorForSavedCondition(representative, schema);
    const selectorField = descriptor?.key || field;
    const action = descriptor?.value_source?.action;
    const fieldConditions = conditions.filter((condition) => condition.field === field);
    const ids = [...new Set(fieldConditions.flatMap((condition) => (
      Array.isArray(condition.value) ? condition.value : [condition.value]
    )).filter((value) => value !== '' && value != null).map(String))];
    if (!action || !ids.length) return [field, {}];
    if (action === 'product_selector.search') {
      const result = await dispatch(resolveProducts(ids));
      if (!resolveProducts.fulfilled.match(result)) return [field, {}];
      return [field, Object.fromEntries((result.payload.items || []).map((item) => [
        String(item.id), item.label || item.name || item.reference || `Product ${item.id}`,
      ]))];
    }
    const initial = await dispatch(searchMerchandisingValues({
      action, field: selectorField, query: '', limit: 50,
    }));
    if (!searchMerchandisingValues.fulfilled.match(initial)) return [field, {}];
    const labels = Object.fromEntries((initial.payload.items || []).map((item) => [
      String(item.id), item.label || item.name || item.breadcrumb,
    ]));
    const unresolved = ids.filter((id) => !labels[id]).slice(0, 10);
    const resolved = await runWithConcurrency(unresolved, id => dispatch(searchMerchandisingValues({
      action, field: selectorField, query: id, limit: 20,
    })), { concurrency: 2 });
    resolved.forEach((entry) => {
      if (entry.status !== 'succeeded') return;
      const result = entry.value;
      if (!searchMerchandisingValues.fulfilled.match(result)) return;
      (result.payload.items || []).forEach((item) => {
        labels[String(item.id)] = item.label || item.name || item.breadcrumb;
      });
    });
    return [field, labels];
  }, { concurrency: 4 });
  const entries = fieldResults
    .filter((entry) => entry.status === 'succeeded')
    .map((entry) => entry.value);
  return Object.fromEntries(entries);
};

const RankingLibrary = ({ basePath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const page = useSelector(selectActiveRulePage);
  const schema = useSelector((state) => state.merchandising.discovery.schema?.condition_fields || {});
  const request = useSelector((state) => state.merchandising.rules.request);
  const lifecycleById = useSelector((state) => state.merchandising.rules.lifecycleById);
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState([]);
  const [titleLabels, setTitleLabels] = useState({});
  const [deleteCandidates, setDeleteCandidates] = useState([]);

  const load = useCallback(() => dispatch(fetchMerchandisingRules({
    filters: {
      phase: 'index', kind: 'product_ranking', language_scope: 'neutral', limit: 100,
    },
  })), [dispatch]);
  useEffect(() => { load(); }, [load]);
  const firstTitleConditions = page.items
    .map((rule) => flattenConditions(rule.conditions)[0])
    .filter(Boolean);
  const titleLookupFingerprint = JSON.stringify(firstTitleConditions.map(conditionLabelKey));
  useEffect(() => {
    let current = true;
    if (!firstTitleConditions.length) return () => { current = false; };
    resolveRankingTitleLabels(dispatch, firstTitleConditions, schema).then((labels) => {
      if (current) setTitleLabels(labels);
    });
    return () => { current = false; };
  // The serialized first-condition set is the lookup identity; selector arrays are not stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, schema, titleLookupFingerprint]);
  const rows = page.items.filter((rule) => (
    rule.status !== 'archived'
    && (status === 'all' || rule.status === status)
    && `${rankingRuleTitle(rule, schema, titleLabels, C.PRODUCT_RANKING)} ${rule.description || ''}`.toLowerCase().includes(filter.toLowerCase())
  ));
  const allVisible = rows.length > 0 && rows.every((row) => selected.includes(row.id));
  const viewState = request.status === 'loading' && !page.items.length
    ? 'loading' : (request.status === 'error' ? 'error' : (rows.length ? 'ready' : 'empty'));
  const lifecycle = async (target) => {
    const eligible = selected.filter((id) => {
      const current = page.items.find((item) => item.id === id)?.status;
      if (target === 'active') return ['draft', 'suggested', 'disabled'].includes(current);
      if (target === 'disabled') return current === 'draft' || current === 'active';
      return false;
    });
    const results = await Promise.all(eligible.map((id) => {
      const rule = page.items.find((item) => item.id === id);
      if (!rule) return null;
      return dispatch(changeMerchandisingRuleStatus({
        id, revision: rule.revision, status: target, rule,
      }));
    }).filter(Boolean));
    if (results.every((result) => !result?.error)) {
      setSelected([]);
      load();
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
  };
  const selectedRules = selected
    .map((id) => page.items.find((item) => item.id === id))
    .filter(Boolean);
  const deleteCandidateIds = deleteCandidates.map((rule) => rule.id);
  const lifecycleError = [...new Set([...selected, ...deleteCandidateIds])]
    .map((id) => lifecycleById[id]?.error)
    .find(Boolean);
  const deleteBusy = deleteCandidateIds.some((id) => lifecycleById[id]?.status === 'loading');
  const canActivate = selectedRules.some((rule) => ['draft', 'suggested', 'disabled'].includes(rule.status));
  const canDisable = selectedRules.some((rule) => ['draft', 'active'].includes(rule.status));
  const canDelete = selectedRules.length > 0;
  const confirmDelete = async () => {
    if (deleteBusy) return;
    const results = await Promise.all(deleteCandidates.map((rule) => dispatch(
      deleteMerchandisingRule({ id: rule.id, revision: rule.revision }),
    )));
    const deletedIds = results.reduce((ids, result, index) => (
      deleteMerchandisingRule.fulfilled.match(result)
        ? [...ids, deleteCandidates[index].id]
        : ids
    ), []);
    if (deletedIds.length > 0) {
      setSelected((current) => current.filter((id) => !deletedIds.includes(id)));
      setDeleteCandidates((current) => current.filter((rule) => !deletedIds.includes(rule.id)));
      load();
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
    if (deletedIds.length === deleteCandidates.length) {
      setDeleteCandidates([]);
    }
  };

  return (
    <div>
      <PageHeader heading={C.PRODUCT_RANKING} body={C.PRODUCT_RANKING_DESCRIPTION} primaryLabel={C.CREATE_RANKING_RULE} onPrimary={() => history.push(`${basePath}/products/new`)} secondary={<><IndexStatusBar inline /><RuleTransferControls selectedRules={[]} onComplete={load} actions={['import']} /></>} />
      {(request.error || lifecycleError) && <div className="mp-inline-error" role="alert">{(request.error || lifecycleError).message}</div>}
      <StateBoundary state={viewState} onReady={load} copy={C} emptyActionLabel={C.CREATE_RANKING_RULE} onEmptyAction={() => history.push(`${basePath}/products/new`)}>
        <section className="mp-library-card" aria-label={C.PRODUCT_RANKING}>
          <div className="mp-library-toolbar">
            <SearchInput value={filter} onChange={setFilter} placeholder={C.RANKING_RULES_PLACEHOLDER} />
            <label className="mp-select-field"><span className="sr-only">{C.STATUS}</span><Select value={status} onChange={(event) => setStatus(event.target.value)} variant="outlined"><MenuItem value="all">{C.ALL_STATUSES}</MenuItem><MenuItem value="active">{C.ACTIVE}</MenuItem><MenuItem value="draft">{C.DRAFT}</MenuItem><MenuItem value="disabled">{C.DISABLED}</MenuItem></Select></label>
            <RuleTransferControls selectedRules={selectedRules} actions={['export']} />
          </div>
          <div className="mp-table-scroll"><div className="mp-rule-table mp-rule-table--ranking" role="table">
            <div className="mp-rule-row mp-rule-row--head" role="row"><span role="columnheader"><SelectionCheckbox checked={allVisible} label={C.SELECT_ALL} onChange={() => setSelected(allVisible ? [] : rows.map((row) => row.id))} /></span><span role="columnheader">{C.APPLIES_TO}</span><span role="columnheader">{C.EFFECT}</span><span role="columnheader">{C.STATUS}</span><span role="columnheader">{C.UPDATED}</span><span role="columnheader" /></div>
            {rows.map((rule) => {
              const title = rankingRuleTitle(rule, schema, titleLabels, C.PRODUCT_RANKING);
              return (
              <div className="mp-rule-row" role="row" key={rule.id}>
                <span role="cell"><SelectionCheckbox checked={selected.includes(rule.id)} label={t('merchandising.rule.select', { name: title })} onChange={() => setSelected((current) => current.includes(rule.id) ? current.filter((id) => id !== rule.id) : [...current, rule.id])} /></span>
                <span role="cell" className="mp-rule-primary"><Link to={`${basePath}/products/${rule.id}`}>{title}</Link></span>
                <span role="cell">
                  {rule.actions?.some((action) => action.type === 'score.multiply') && (
                    <PriorityLabel factor={Number(rule.actions.find((action) => action.type === 'score.multiply').value)} copy={C} />
                  )}
                  {rule.actions?.filter((action) => action.type === 'search_terms.add').map((action) => (
                    <span className="mp-ranking-rule-effect" key={action.priority}>
                      {t('merchandising.workbench.searchTerms')} · {t(`merchandising.workbench.searchTerms.${action.priority}`)}
                    </span>
                  ))}
                </span>
                <span role="cell"><StatusChip status={rule.status} copy={C} /></span>
                <span role="cell" className="mp-muted">{formatLocalDateTime(rule.updated_at, intl.locale) || '—'}</span>
                <span role="cell" className="mp-row-actions"><IconButton aria-label={t('merchandising.workbench.duplicateNamed', { name: title })} onClick={() => history.push(`${basePath}/products/new?from=${rule.id}`)}><FileCopyOutlinedIcon fontSize="small" /></IconButton><IconButton aria-label={t('merchandising.workbench.deleteNamed', { name: title })} onClick={() => setDeleteCandidates([rule])}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
              </div>
              );
            })}
          </div></div>
        </section>
        {selected.length > 0 && <div className="mp-bulk-bar"><strong>{selected.length} {C.SELECTED}</strong><RuleTransferControls selectedRules={selectedRules} onComplete={load} actions={['duplicate']} />{canActivate && <Button onClick={() => lifecycle('active')}>{t('merchandising.action.activate')}</Button>}{canDisable && <Button onClick={() => lifecycle('disabled')}>{C.DISABLE}</Button>}{canDelete && <Button className="mp-destructive-action" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteCandidates(selectedRules)}>{t('merchandising.action.delete')}</Button>}</div>}
      </StateBoundary>
      <Dialog className="mp-dialog" open={deleteCandidates.length > 0} onClose={() => { if (!deleteBusy) setDeleteCandidates([]); }} maxWidth="xs" fullWidth>
        <DialogTitle>{t('merchandising.workbench.deleteTitle')}</DialogTitle>
        <DialogContent>
          <p>{t('merchandising.workbench.deleteWarning', { count: deleteCandidates.length })}</p>
          {lifecycleError && <div className="mp-inline-error" role="alert">{lifecycleError.message}</div>}
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="outlined" disabled={deleteBusy} onClick={() => setDeleteCandidates([])}>{C.CANCEL}</Button><Button className="mp-danger-button" variant="contained" disabled={deleteBusy} startIcon={deleteBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />} onClick={confirmDelete}>{deleteBusy ? t('merchandising.action.deleting') : t('merchandising.action.delete')}</Button></DialogActions>
      </Dialog>
    </div>
  );
};
RankingLibrary.propTypes = { basePath: PropTypes.string.isRequired };

const RankingEditor = ({ basePath, ruleId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const { productProfile, capabilities = {} } = useShellAppContext();
  const isCloud = productProfile?.id === 'cloud';
  const searchPhrasesAvailable = capabilities.merchandising_search_terms_v1 === true;
  const advancedConditions = capabilities.merchandising_advanced_conditions_v1 === true;
  const schema = useSelector((state) => state.merchandising.discovery.schema?.condition_fields || {});
  const entities = useSelector((state) => state.merchandising.rules.entities);
  const editorRequest = useSelector((state) => state.merchandising.editor.request);
  const previewState = useSelector((state) => state.merchandising.previews.index);
  const [model, setModel] = useState(emptyRankingWorkbench);
  const [baseline, setBaseline] = useState(emptyRankingWorkbench);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [previewQueued, setPreviewQueued] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [resolvedProducts, setResolvedProducts] = useState({});
  const [conditionLabels, setConditionLabels] = useState({});
  const [localError, setLocalError] = useState(null);
  const [priorityPosition, setPriorityPosition] = useState(
    () => rankingFactorToPosition(emptyRankingWorkbench().factor),
  );
  const loaded = useRef(null);
  const previewGeneration = useRef(0);
  const duplicateId = new URLSearchParams(location.search).get('from');
  const sourceId = ruleId || duplicateId;
  const filterOptions = useMemo(() => filterIndexFields(schema, filterQuery)
    .map(option => ({
      ...option,
      descriptor: descriptorForCapabilities(option.descriptor, advancedConditions),
    }))
    .filter(option => option.descriptor.operators.length > 0), [
    advancedConditions, filterQuery, schema,
  ]);
  const openFilterDialog = () => {
    setFilterQuery('');
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    setLocalError(null);
    setPreviewPage(1);
    setConditionLabels({});
    setFilterDialogOpen(false);
    setScheduleEnabled(false);
    setPreviewQueued(false);
    dispatch(clearRuleEditorFeedback());
    dispatch(clearPreview('index'));
    loaded.current = null;
    if (!sourceId) {
      const empty = emptyRankingWorkbench();
      setModel(empty);
      setBaseline(empty);
      return;
    }
    if (!entities[sourceId]) dispatch(fetchMerchandisingRule({ id: sourceId }));
  // A route identity change always starts a fresh editor session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sourceId]);
  useEffect(() => {
    const rule = sourceId ? entities[sourceId] : null;
    if (!rule || loaded.current === sourceId) return;
    const next = rankingRuleToWorkbench(rule);
    if (!ruleId) {
      next.id = null;
      next.revision = null;
      next.status = 'draft';
      next.name = '';
    }
    setModel(next);
    setBaseline(next);
    setScheduleEnabled(Boolean(next.validFrom || next.validTo));
    loaded.current = sourceId;
  }, [entities, ruleId, sourceId]);
  useEffect(() => {
    setPriorityPosition(rankingFactorToPosition(model.factor));
  }, [model.factor]);

  const titleCondition = flattenConditions(model.conditions)[0] || null;
  const titleConditionFingerprint = titleCondition ? conditionLabelKey(titleCondition) : '';
  useEffect(() => {
    let current = true;
    if (!titleCondition) return () => { current = false; };
    resolveRankingTitleLabels(dispatch, [titleCondition], schema).then((labels) => {
      if (current) setConditionLabels((existing) => ({ ...existing, ...labels }));
    });
    return () => { current = false; };
  // The serialized field/value pair is the label lookup identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, schema, titleConditionFingerprint]);

  const unavailableSearchTerms = !searchPhrasesAvailable
    && (Boolean(model.boostedTermsText?.trim()) || Boolean(model.midTermsText?.trim()));
  const unavailableAdvancedConditions = !advancedConditions
    && ruleUsesAdvancedConditions({ conditions: model.conditions }, schema);
  const valid = !unavailableSearchTerms && !unavailableAdvancedConditions
    && rankingExpressionValid(model.conditions)
    && Number.isFinite(model.factor) && model.factor >= 0.1 && model.factor <= 5
    && (!searchPhrasesAvailable || (rankingSearchTermsValid(model.boostedTermsText)
      && rankingSearchTermsValid(model.midTermsText)))
    && (model.factor !== 1 || (searchPhrasesAvailable
      && (Boolean(model.boostedTermsText?.trim()) || Boolean(model.midTermsText?.trim()))));
  const generatedName = rankingRuleTitle(
    { name: '', conditions: model.conditions }, schema, conditionLabels, C.PRODUCT_RANKING,
  );
  const displayName = model.name.trim() || generatedName || C.PRODUCT_RANKING;
  // Old imports used translated facet labels as storage keys. Keep reading those
  // rules, but preview and persist the schema's stable key so every editor and
  // indexing client sees one canonical rule shape.
  const canonicalConditions = useMemo(
    () => canonicalizeSavedConditionFields(model.conditions, schema),
    [model.conditions, schema],
  );
  const draft = useMemo(
    () => rankingWorkbenchRule({ ...model, conditions: canonicalConditions }),
    [canonicalConditions, model],
  );
  const fingerprint = JSON.stringify(draft);
  useEffect(() => {
    const ownGeneration = ++previewGeneration.current;
    if (!valid) {
      setPreviewQueued(false);
      dispatch(clearPreview('index'));
      return undefined;
    }
    setPreviewQueued(true);
    const timer = window.setTimeout(async () => {
      await dispatch(runMerchandisingPreview({
        workspace: 'index',
        payload: { draft_rule: draft, sample_limit: 50 },
      }));
      if (previewGeneration.current === ownGeneration) setPreviewQueued(false);
    }, 650);
    return () => {
      window.clearTimeout(timer);
      if (previewGeneration.current === ownGeneration) previewGeneration.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fingerprint, valid]);
  const samples = useMemo(() => previewState.result?.samples || [], [previewState.result]);
  const sampleIds = useMemo(
    () => samples.map((sample) => String(sample.product_id)),
    [samples],
  );
  const previewBusy = previewQueued
    || previewState.request.refreshing
    || previewState.request.status === 'loading';
  useEffect(() => {
    if (!sampleIds.length) { setResolvedProducts({}); return; }
    dispatch(resolveProducts(sampleIds)).then((result) => {
      if (!resolveProducts.fulfilled.match(result)) return;
      setResolvedProducts(Object.fromEntries((result.payload.items || []).map((product) => [String(product.id), product])));
    });
  }, [dispatch, sampleIds]);

  const productApi = useMemo(() => ({
    searchProducts: async (query, limit) => {
      const result = await dispatch(searchProducts({ query, limit }));
      if (searchProducts.fulfilled.match(result)) return result.payload;
      throw new Error(result.payload || 'Product search failed.');
    },
    resolveProducts: async (ids) => {
      const result = await dispatch(resolveProducts(ids));
      if (resolveProducts.fulfilled.match(result)) return result.payload;
      throw new Error(result.payload || 'Product resolve failed.');
    },
  }), [dispatch]);
  const dirty = JSON.stringify(model) !== JSON.stringify(baseline);
  const unsavedGuard = useUnsavedChangesGuard(dirty);
  const readOnly = model.status === 'archived';
  const active = model.status === 'active';
  const scheduleIsValid = isCloud || validSchedule(model.validFrom, model.validTo);
  const save = async (publish) => {
    if (!valid) { setLocalError(t('merchandising.workbench.rankingIncomplete')); return; }
    if (!scheduleIsValid) { setLocalError(t('merchandising.workbench.invalidSchedule')); return; }
    setLocalError(null);
    const savedAction = await dispatch(saveMerchandisingRule({
      ...(model.id ? { id: model.id, revision: model.revision } : { clientMutationId: uuid() }),
      rule: draft,
    }));
    if (!saveMerchandisingRule.fulfilled.match(savedAction)) return;
    let saved = savedAction.payload.rule || savedAction.payload;
    if (publish && saved.status !== 'active') {
      const activated = await dispatch(changeMerchandisingRuleStatus({
        id: saved.id, revision: saved.revision, status: 'active', rule: saved,
      }));
      if (!changeMerchandisingRuleStatus.fulfilled.match(activated)) return;
      saved = activated.payload.rule || activated.payload;
    }
    const canonical = rankingRuleToWorkbench(saved);
    setModel(canonical);
    setBaseline(canonical);
    loaded.current = String(saved.id);
    unsavedGuard.navigateWithoutPrompt(() => history.replace(`${basePath}/products/${saved.id}`));
  };

  const renderGroup = group => <ConditionBuilder conditions={group} schema={schema} advancedConditions={advancedConditions} productApi={productApi} onChange={conditions => setModel(current => ({ ...current, conditions }))} onItems={(id, items) => setConditionLabels(current => ({ ...current, [id]: { ...(current[id] || {}), ...Object.fromEntries(items.map(entry => [String(entry.id), entry.label || entry.name || entry.breadcrumb || String(entry.id)])) } }))} />;
  const visibleSamples = samples.slice((previewPage - 1) * 10, previewPage * 10);
  const pages = Math.max(1, Math.ceil(samples.length / 10));
  useEffect(() => {
    if (previewPage > pages) setPreviewPage(pages);
  }, [pages, previewPage]);

  return (
    <div>
      <PageHeader
        eyebrow={<Link className="mp-back-link" to={`${basePath}/products`}><ArrowBackIcon />{C.BACK_TO_RANKING}</Link>}
        heading={model.id ? displayName : C.CREATE_RANKING_RULE}
        body={null}
        primaryLabel={active ? t('merchandising.action.saveChanges') : C.PUBLISH}
        primaryIcon={active ? <SaveIcon /> : <PublishIcon />}
        primaryDisabled={readOnly || !valid || !scheduleIsValid || (active && !dirty)}
        onPrimary={() => save(!active)}
        secondary={<>{dirty && <Button variant="outlined" onClick={() => { setModel(baseline); setScheduleEnabled(Boolean(baseline.validFrom || baseline.validTo)); }}>{C.RESET_CHANGES}</Button>}{!active && dirty && <Button variant="outlined" disabled={readOnly || !valid || !scheduleIsValid} onClick={() => save(false)}>{model.status === 'draft' ? t('merchandising.action.saveDraft') : t('merchandising.action.saveChanges')}</Button>}<IndexStatusBar inline /></>}
      />
      {readOnly && <div className="mp-inline-error" role="status">{t('merchandising.workbench.archivedRanking')}</div>}
      {(localError || editorRequest.error || previewState.request.error) && (
        <InlineNotice
          dismissLabel={C.CLOSE}
          onDismiss={() => {
            setLocalError(null);
            dispatch(clearRuleEditorFeedback());
            if (previewState.request.error) dispatch(clearPreview('index'));
          }}
        >
          {localError || editorRequest.error?.message || previewState.request.error?.message}
        </InlineNotice>
      )}
      {(unavailableSearchTerms || unavailableAdvancedConditions) && (
        <InlineNotice>{t('merchandising.capability.unavailable')}</InlineNotice>
      )}
      <div className="mp-ranking-workbench">
        <aside className="mp-definition-panel">
          <section className="mp-rule-identity">
            <label className="mp-plain-field">
              <span>{t('merchandising.workbench.ruleNameOptional')}</span>
              <input
                type="text"
                maxLength={160}
                disabled={readOnly}
                value={model.name}
                placeholder={generatedName || t('merchandising.workbench.generatedRuleName')}
                onChange={(event) => setModel({ ...model, name: event.target.value })}
              />
            </label>
          </section>
          <section>
            <div className="mp-section-heading"><h2>{t('merchandising.workbench.productFilters')}</h2></div>
            {model.conditions.items.length ? renderGroup(model.conditions) : <button type="button" className="mp-condition-empty" onClick={openFilterDialog}><span aria-hidden="true"><AddIcon /></span><strong>{t('merchandising.workbench.addFilter')}</strong></button>}
            {model.conditions.items.length > 0 && <div className="mp-inline-actions"><Tooltip title={t('merchandising.workbench.addFilter')}><IconButton className="mp-add-filter-button" aria-label={t('merchandising.workbench.addFilter')} onClick={openFilterDialog}><AddIcon /></IconButton></Tooltip></div>}
          </section>
          <section>
            <div className="mp-section-heading"><h2>{t('merchandising.workbench.productPriority')}</h2><PriorityLabel factor={model.factor} copy={C} /></div>
            <Slider
              className="mp-bipolar-slider"
              min={0}
              max={100}
              step={0.1}
              marks={rankingFactorMarks}
              disabled={readOnly}
              value={priorityPosition}
              onChange={(event, value) => setPriorityPosition(value)}
              onChangeCommitted={(event, value) => {
                const factor = rankingPositionToFactor(value);
                setPriorityPosition(rankingFactorToPosition(factor));
                setModel(current => (current.factor === factor ? current : { ...current, factor }));
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={value => `${rankingPositionToFactor(value)}×`}
              getAriaValueText={value => `${rankingPositionToFactor(value)}×`}
              aria-label={t('merchandising.workbench.productPriority')}
            />
            <div className="mp-scale-labels" aria-hidden="true">{[
              { factor: 0.25, label: t('merchandising.workbench.priority.extraLow') },
              { factor: 0.5, label: t('merchandising.workbench.priority.lower') },
              { factor: 1, label: t('merchandising.workbench.priority.natural') },
              { factor: 2, label: t('merchandising.workbench.priority.higher') },
              { factor: 4, label: t('merchandising.workbench.priority.extraHigh') },
            ].map(({ factor, label }) => <span key={factor} style={{ '--mp-scale-position': `${rankingFactorToPosition(factor)}%` }}><strong>{label}</strong><small>{factor}{t('merchandising.workbench.multiplierSymbol')}</small></span>)}</div>
          </section>
          {searchPhrasesAvailable && <SearchPhraseEditor
            key={sourceId || 'new'}
            boostedTermsText={model.boostedTermsText}
            midTermsText={model.midTermsText}
            disabled={readOnly}
            onChange={values => setModel(current => ({ ...current, ...values }))}
          />}
          {!isCloud && (
            <ScheduleCard
              enabled={scheduleEnabled}
              validFrom={model.validFrom}
              validTo={model.validTo}
              disabled={readOnly}
              copy={C}
              onEnabledChange={(enabled) => {
                setScheduleEnabled(enabled);
                if (!enabled) setModel((currentModel) => ({
                  ...currentModel, validFrom: null, validTo: null,
                }));
              }}
              onChange={(field, value) => setModel((currentModel) => ({
                ...currentModel, [field]: value,
              }))}
            />
          )}
        </aside>
        <main className="mp-ranking-preview">
          <div className="mp-ranking-preview-header"><h2>{t('merchandising.preview.title')}</h2><span className="mp-result-count"><strong>{previewState.result?.count_kind === 'bounded_lower_bound' ? '≥ ' : ''}{previewState.result?.matched_count || 0}</strong> {t('merchandising.workbench.matching')}</span></div>
          <div className="mp-ranking-product-list" aria-busy={previewBusy}>
            {visibleSamples.map((sample, index) => {
              const product = resolvedProducts[String(sample.product_id)] || { id: sample.product_id, name: `Product #${sample.product_id}`, thumbnail: '' };
              const searchTerms = sample.evaluation?.search_terms || {};
              return <article key={sample.product_id}><span className="mp-rank-number">{(previewPage - 1) * 10 + index + 1}</span><ProductImage product={product} size="thumb" fit="contain" /><div><strong>{product.name}</strong><span>{product.reference || product.vendorCode || `#${sample.product_id}`}</span>{isCloud && ['boosted', 'mid'].map((priority) => (
                searchTerms[priority]?.length ? <span className="mp-ranking-search-term-preview" key={priority} title={searchTerms[priority].join(' · ')}>{t(`merchandising.workbench.searchTerms.${priority}`)}: {searchTerms[priority].slice(0, 2).join(' · ')}{searchTerms[priority].length > 2 ? ` +${searchTerms[priority].length - 2}` : ''}</span> : null
              ))}</div><PriorityLabel factor={model.factor} copy={C} /></article>;
            })}
          </div>
          {samples.length > 0 && <div className="mp-preview-pagination"><span>{t('merchandising.workbench.previewProducts', { count: samples.length })}</span>{pages > 1 && <WorkbenchPagination page={previewPage} pageCount={pages} onChange={setPreviewPage} firstLabel={C.FIRST_PAGE} previousLabel={C.PREVIOUS_PAGE} nextLabel={C.NEXT_PAGE} lastLabel={C.LAST_PAGE} />}</div>}
          {previewBusy && <div className="mp-preview-loading" role="status" aria-live="polite"><CircularProgress size={28} thickness={4} /><strong>{C.PREVIEW_REFRESHING}</strong></div>}
        </main>
      </div>
      <ConditionFieldPicker open={filterDialogOpen} query={filterQuery} onQueryChange={setFilterQuery} options={filterOptions} onClose={() => setFilterDialogOpen(false)} onPick={({ field, descriptor }) => { const operator = preferredOperator(descriptor); setModel(current => ({ ...current, conditions: appendRankingCondition(current.conditions, { field, operator, value: defaultConditionValue(descriptor, operator) }) })); setFilterDialogOpen(false); }} />
      <UnsavedChangesDialog guard={unsavedGuard} />
    </div>
  );
};
RankingEditor.propTypes = { basePath: PropTypes.string.isRequired, ruleId: PropTypes.string };
RankingEditor.defaultProps = { ruleId: null };

const ProductRankingWorkspace = ({ basePath, editor, ruleId }) => {
  const enabled = useSelector((state) => state.merchandising.capability.rules);
  return (
    <CapabilityNotice enabled={enabled}>
      {editor
        ? <RankingEditor basePath={basePath} ruleId={ruleId} />
        : <RankingLibrary basePath={basePath} />}
    </CapabilityNotice>
  );
};
ProductRankingWorkspace.propTypes = { basePath: PropTypes.string.isRequired, editor: PropTypes.bool, ruleId: PropTypes.string };
ProductRankingWorkspace.defaultProps = { editor: false, ruleId: null };

export default ProductRankingWorkspace;
